/**
 * ToolbarActionButton — shared chrome for request-toolbar action buttons so they line up
 * with the Print/PDF trigger and each other (icon + label button).
 *
 * Proportions intentionally mirror `DevicesViewToolbarButton` ("View Devices") in
 * LocationTagsControl.tsx: same 36px web height, padding, gap, icon + text sizes, and
 * theme tokens. The corner radius is `spacing.toolbarActionBorderRadius` (web) /
 * `toolbarActionBorderRadiusNative` (native), shared with Location Tags and PDF.
 * Used for the Version History entry point (#1148); reuse for any new trailing
 * toolbar action rather than hand-rolling inline styles.
 */

import React from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';

/** Fixed height so toolbar actions (PDF, View Devices, Version History) align on web. */
export const REQUEST_TOOLBAR_ACTION_HEIGHT = 36;

export interface ToolbarActionButtonProps {
  label: string;
  onPress: () => void;
  /** Icon component from `../icons` (e.g. RotateCcw), rendered at the toolbar icon size. */
  icon?: React.ComponentType<{ size?: number; color?: string }>;
  disabled?: boolean;
  /** Active/toggled chrome (accent fill + border), matching View Devices' selected state. */
  selected?: boolean;
  /**
   * For a button that reveals or hides a region (the Discussion panel):
   * announced as expanded/collapsed. Left out of `accessibilityState` when
   * undefined, so plain actions keep announcing only `disabled`/`selected`.
   */
  expanded?: boolean;
  testID?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
}

/**
 * Runtime styles so Platform.OS is not frozen at StyleSheet.create time (matches the
 * DevicesViewToolbarButton pattern and avoids the iOS native-build staleness trap).
 */
function getContainerStyle(): ViewStyle {
  const isWeb = Platform.OS === 'web';
  return {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isWeb ? 6 : 4,
    minHeight: isWeb ? REQUEST_TOOLBAR_ACTION_HEIGHT : 28,
    height: isWeb ? REQUEST_TOOLBAR_ACTION_HEIGHT : undefined,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    // One toolbar shape per platform, following Location Tags (was a 999 pill on native).
    borderRadius: isWeb
      ? spacing.toolbarActionBorderRadius
      : spacing.toolbarActionBorderRadiusNative,
    paddingVertical: isWeb ? 0 : 6,
    paddingHorizontal: isWeb ? 12 : 8,
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  } as ViewStyle;
}

function getTextStyle() {
  const isWeb = Platform.OS === 'web';
  return {
    fontSize: isWeb ? 14 : 11,
    lineHeight: isWeb ? 18 : 14,
    fontWeight: '500' as const,
    color: colors.textPrimary,
  };
}

export const ToolbarActionButton: React.FC<ToolbarActionButtonProps> = ({
  label,
  onPress,
  icon: Icon,
  disabled = false,
  selected = false,
  expanded,
  testID,
  accessibilityLabel,
  accessibilityHint,
}) => {
  const isWeb = Platform.OS === 'web';
  const iconSize = isWeb ? 14 : 12;
  const iconColor = disabled ? colors.textMuted : colors.primary;

  return (
    <TouchableOpacity
      style={[
        getContainerStyle(),
        selected && styles.selected,
        disabled && styles.disabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityState={
        expanded === undefined
          ? { disabled, selected }
          : { disabled, selected, expanded }
      }
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint}
    >
      {Icon ? <Icon size={iconSize} color={iconColor} /> : null}
      <Text
        style={[
          getTextStyle(),
          selected && styles.textSelected,
          disabled && styles.textDisabled,
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  selected: {
    backgroundColor: colors.surfaceAccentMuted,
    borderColor: colors.primary,
  },
  disabled: { opacity: 0.55 },
  textSelected: { color: colors.primary, fontWeight: '600' },
  textDisabled: { color: colors.textMuted },
});
